import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { AsyncPipe, DatePipe, DecimalPipe } from '@angular/common';
import {
  TuiButtonModule,
  TuiDataListModule,
  TuiDialogService,
  TuiDropdownModule,
  TuiLoaderModule,
} from '@taiga-ui/core';
import { tuiIsFalsy, TuiLetModule } from '@taiga-ui/cdk';
import { TuiTableModule } from '@taiga-ui/addon-table';
import {
  catchError,
  EMPTY,
  filter,
  map,
  merge,
  Observable,
  share,
  startWith,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PolymorpheusComponent } from '@tinkoff/ng-polymorpheus';
import { InvoiceService } from '@shared/services/invoice.service';
import { SpaceNumberPipe } from '@shared/pipes/space-number.pipe';
import { AlertService } from '@shared/services/alert.service';
import { DocumentVisibilityService } from '@shared/services/document-visibility.service';
import { TuiDataListDropdownManagerModule } from '@taiga-ui/kit';
import { OnDropdownOpenDirective } from '@shared/directives/on-dropdown-open.directive';
import { WebsocketService } from '@shared/services/websocket.service';
import { InvoiceDto, ResolutionStrategy } from '../../../../types/invoice';
import { UploadAccountingInvoicesComponent } from './components/upload-accounting-invoices/upload-accounting-invoices.component';
import { Action } from '../../../../types/action';
import { WebsocketEvent } from '../../../../types/websocket';
import { AccountingProcessesComponent } from './components/accounting-processes/accounting-processes.component';

@Component({
  selector: 'app-accounting-invoices-journal',
  standalone: true,
  imports: [
    AsyncPipe,
    DatePipe,
    DecimalPipe,
    TuiButtonModule,
    TuiLetModule,
    TuiLoaderModule,
    TuiTableModule,
    SpaceNumberPipe,
    TuiDataListDropdownManagerModule,
    TuiDataListModule,
    OnDropdownOpenDirective,
    TuiDropdownModule,
    AccountingProcessesComponent,
  ],
  templateUrl: './accounting-invoices-journal.component.html',
  styleUrl: './accounting-invoices-journal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountingInvoicesJournalComponent implements OnInit, OnDestroy {
  openDialog$ = new Subject<void | InvoiceDto>();
  fetch$ = new Subject<void>();

  getSourceFile$ = new Subject<number>();

  downloadExcel$ = new Subject<number>();
  downloadGroupExcel$ = new Subject<number>();

  tableHeaders: Array<{
    name: string;
    value: keyof InvoiceDto;
  }> = [
    { name: 'ID', value: 'id' },
    { name: 'Группа', value: 'groupId' },
    { name: 'Номер', value: 'number' },
    { name: 'Дата', value: 'date' },
    { name: 'Продавец', value: 'seller' },
    { name: 'Покупатель', value: 'buyer' },
    { name: 'Стоимость', value: 'cost' },
  ];

  readonly request$ = merge(
    this.fetch$,
    this.documentVisibilityService.visible$.pipe(startWith(null)),
  ).pipe(
    switchMap(() =>
      this.invoiceService.getAllInvoices({
        resolutionStrategy: ResolutionStrategy.ACCOUNTING,
      }),
    ),
    share(),
    takeUntilDestroyed(this.destroyRef),
  );

  readonly items$: Observable<InvoiceDto[]> = this.request$.pipe(startWith([]));

  readonly loading$ = this.request$.pipe(map(tuiIsFalsy));

  protected readonly Action = Action;
  constructor(
    private readonly invoiceService: InvoiceService,
    private readonly destroyRef: DestroyRef,
    private readonly dialogs: TuiDialogService,
    private readonly alert: AlertService,
    private readonly documentVisibilityService: DocumentVisibilityService,
    private readonly ws: WebsocketService,
  ) {}

  onOpenDialog() {
    return this.openDialog$.pipe(
      switchMap(() =>
        this.dialogs.open(
          new PolymorpheusComponent(UploadAccountingInvoicesComponent),
          {
            size: 'auto',
          },
        ),
      ),
      tap(() => this.fetch$.next()),
    );
  }

  onGetSourceFile() {
    return this.getSourceFile$.pipe(
      switchMap(id =>
        this.invoiceService.getSourceFile(id).pipe(
          catchError(err => {
            this.alert.showError(err.error);
            return EMPTY;
          }),
        ),
      ),
    );
  }

  onDownloadExcel() {
    return this.downloadExcel$.pipe(
      switchMap(id =>
        this.invoiceService.exportToXlsxById(id).pipe(
          catchError(err => {
            this.alert.showError(err.error);
            return EMPTY;
          }),
        ),
      ),
    );
  }

  onDownloadGroupExcel() {
    return this.downloadGroupExcel$.pipe(
      switchMap(groupId =>
        this.invoiceService.exportToXlsxByGroupId(groupId).pipe(
          catchError(err => {
            this.alert.showError(err.error);
            return EMPTY;
          }),
        ),
      ),
    );
  }

  get columns() {
    return [...this.tableHeaders.map(header => header.value), 'actions'];
  }

  initSocket() {
    this.ws.connect().then(() => this.ws.listenInvoices());
  }

  ngOnDestroy() {
    this.ws.unsubscribeInvoices();
  }

  refreshInvoicesOnProcessingStatusChange() {
    return this.ws.invoicesEvents$.pipe(
      filter(
        wsEvent =>
          wsEvent.event ===
          WebsocketEvent.INVOICE_ACCOUNTING_PROCESSING_CHANGE_STATUS,
      ),
      tap(() => this.fetch$.next()),
    );
  }

  ngOnInit() {
    merge(
      this.onDownloadExcel(),
      this.onDownloadGroupExcel(),
      this.onGetSourceFile(),
      this.onOpenDialog(),
      this.refreshInvoicesOnProcessingStatusChange(),
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();

    this.initSocket();
  }
}
